import { Component, computed, inject, input, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DataBagService } from '@/common/data-bag.service';
import { DateService } from '@/common/date.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { NoteDialogComponent } from '../note-dialog/note-dialog.component';
import { NoteStore } from '../note-store.service';
import { Note, NoteConverted, Results } from '../note.model';
import { NgClass, NgForOf, NgIf } from '@angular/common';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatRow,
	MatRowDef,
	MatTable,
} from '@angular/material/table';
import { MatButton } from '@angular/material/button';
import { TableName } from '@/common/enums/TableName';
import { map } from 'rxjs';

@Component({
	selector: 'app-note-list',
	templateUrl: './note-list.component.html',
	styleUrls: ['./note-list.component.scss'],
	imports: [
		NgClass,
		NgIf,
		NgForOf,
		MatTable,
		MatColumnDef,
		MatHeaderCellDef,
		MatCellDef,
		MatHeaderRowDef,
		MatRowDef,
		MatRow,
		MatHeaderRow,
		MatButton,
		MatCell,
		MatHeaderCell,
	],
	standalone: true,
})
export class NoteListComponent implements OnInit {
	readonly showHidden = input(true);
	readonly task = input<number>(null);
	readonly editable = input(true);
	readonly disableHiding = input(false);
	readonly view = input.required<string>();
	readonly tableId = input.required<number>();
	readonly tableName = input.required<TableName>();

	readonly visibleNotes = computed(() => {
		return this.convertedNoteList()?.filter((item) => {
			const isVisible = !item.hidden || this.showHidden();
			if (this.task()) {
				return isVisible && item.task === this.task();
			}
			return isVisible;
		});
	});

	readonly haveNotes = computed(() => {
		const list = this.convertedNoteList();
		const currentTask = this.task();
		const selectedTaskIsVisible = currentTask
			? list.some((item) => !item.hidden && item.task === currentTask)
			: true;
		const hasUnhiddenItems = list.some((item) => !item.hidden);
		return selectedTaskIsVisible && (hasUnhiddenItems || this.showHidden);
	});

	readonly convertedNoteList = computed(() => {
		if (this.noteStore.notesList()) {
			return this.convertData(this.noteStore.notesList());
		}
		return [];
	});

	readonly displayedColumns: string[] = ['created', 'content', 'attachment', 'kinds', 'edit', 'hide'];

	protected bag = inject(DataBagService);
	protected date = inject(DateService);
	protected infobox = inject(InfoboxService);
	protected loader = inject(LoaderService);
	protected dialog = inject(MatDialog);
	protected noteStore = inject(NoteStore);

	ngOnInit(): void {
		this.refreshData();
	}

	openNoteDialog(note: NoteConverted | boolean): void {
		this.loader.show();
		this.bag.get('/task_note/kind/?uses__id__in=RES', (kinds: Results, error) => {
			if (!note) {
				kinds.results.forEach((item) => {
					item['isChecked'] = this.tableName() !== TableName.SEASON_TERM; //only season_term is supposed to have everything unchecked
				});
				const elementRef = this.dialog.open(NoteDialogComponent, {
					disableClose: true,
					data: {
						kinds: kinds.results,
						note: note,
						resId: this.tableId(),
						editable: true,
						tableName: this.tableName(),
					},
				});
				this.afterClosedDialog(elementRef, note as NoteConverted);
				this.loader.hide();
				return;
			}
			note = note as NoteConverted;

			this.noteStore
				.fetchNote(note.id)
				.pipe(map((note) => ({ ...note, createdAt: this.date.apiToDateTime(note.createdAt as string) })))
				.subscribe((note: NoteConverted) => {
					kinds.results.forEach((item) => {
						if (note.kinds.find((kind) => kind.id === item.id)) {
							item['isChecked'] = true;
						} else {
							item['isChecked'] = false;
						}
					});
					const elementRef = this.dialog.open(NoteDialogComponent, {
						disableClose: true,
						data: {
							kinds: kinds.results,
							note,
							resId: this.tableId(),
							editable: this.checkEditTime(note),
							tableName: this.tableName(),
						},
					});
					this.afterClosedDialog(elementRef, note);
					this.loader.hide();
				});
		});
	}

	checkEditTime(note: NoteConverted): boolean {
		let diff =
			(new Date(this.date.DateTimeToApi(note.createdAt.date, note.createdAt.time)).getTime() -
				new Date(this.date.DateTimeToApi(this.date.now().date, this.date.now().time)).getTime()) /
			1000;
		diff /= 60;
		return Math.abs(Math.round(diff)) <= 15;
	}

	hideOrShowNote(item, hidden): void {
		item.hidden = hidden;
		item.kinds = item.kinds.map((kind) => {
			return kind.id;
		});
		this.bag.put(`/task_note/note/${item.id}/`, item, (result, error) => {
			if (error) {
				this.loader.hide();
				return;
			}
			this.refreshData();
		});
	}

	private afterClosedDialog(elementRef: MatDialogRef<NoteDialogComponent>, note: NoteConverted): void {
		elementRef.afterClosed().subscribe((noteToSend: Note) => {
			if (noteToSend === undefined) {
				return;
			}
			noteToSend.task = this.task();
			noteToSend.tableId = this.tableId();
			if (!note) {
				this.bag.post('/task_note/note/', noteToSend, (result, error) => {
					if (error) {
						this.loader.hide();
						return;
					}
					this.refreshData();
				});
				return;
			}
			this.bag.put(`/task_note/note/${note.id}/`, noteToSend, (result, error) => {
				if (error) {
					this.loader.hide();
					return;
				}
				this.refreshData();
			});
		});
	}

	private convertData(data: Note[]): NoteConverted[] {
		if (data.some((item) => typeof item.createdAt === 'object')) {
			return data as unknown as NoteConverted[];
		}

		return data
			.slice()
			.sort((a, b) => (a.createdAt as string).localeCompare(b.createdAt as string))
			.map((item) => {
				const createdAt = this.date.apiToDateTime(item.createdAt as string);

				const base: Omit<NoteConverted, 'isEditable'> = {
					...item,
					createdAt,
				};

				return {
					...base,
					isEditable: this.checkEditTime(base as NoteConverted),
				};
			});
	}

	private refreshData() {
		this.noteStore.loadNotes(this.tableId(), this.tableName());
	}
}
